import { I18n } from "@assets/strings";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { useOrganizationTracks } from "@domain/organization/hooks";
import { Track } from "@domain/organization/track/track";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { Description } from "@ui/components/containers/description";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Row, Stack, row, stack } from "@ui/components/layout";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, mobile } from "@ui/styles/utils";
import { ShareroomBlocIcons } from "@ui/pages/group/shareroom/shareroomInfo";
import { TrackDetails } from "./trackDetails";
import { TrackEditorModal } from "./trackEditorModal";
import { TrackView } from "./trackView";

export interface OrganizationTracksRef {
	reset: () => void;
}
interface OrganizationTracksProps extends React.HTMLAttributes<HTMLDivElement> {
	organizationId: string;
}
export const OrganizationTracks = forwardRef<OrganizationTracksRef, OrganizationTracksProps>(
	({ organizationId, ...htmlProps }, ref) => {
		const { result: tracks, loading } = useOrganizationTracks(organizationId);
		const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
		const { createModal } = useModalTools();
		const alias = useOrganizationAlias();

		useImperativeHandle(ref, () => ({
			reset: () => setSelectedTrack(null),
		}));

		// if (!tracks) {
		// 	return null;
		// }
		return loading ? (
			<ClipLoader />
		) : (
			<Container {...htmlProps}>
				<Row gap={10} justify="space-between">
					<Breadcrumb>
						<StyledPrimarybold onClick={() => setSelectedTrack(null)}>
							{I18n.t("organization.tab.tracks")}
						</StyledPrimarybold>
						{selectedTrack && (
							<>
								<span>{" > "}</span>
								<TrackTitle>{selectedTrack.name}</TrackTitle>
							</>
						)}
					</Breadcrumb>
					{!selectedTrack && alias && (
						<RoundedPlusButton
							onClick={() => createModal((close) => <TrackEditorModal organizationId={organizationId} close={close} />)}
						/>
					)}
				</Row>
				{selectedTrack ? (
					<TrackDetails trackId={selectedTrack.id} />
				) : tracks.length === 0 ? (
					<Stack style={{ width: "100%" }} gap={10} align="center" justify="center">
						<IconView>
							<img src={ShareroomBlocIcons["presentation"]} />
						</IconView>

						<Description>
							{I18n.t(`organization.tracks.presentation.${alias ? "owner" : "member"}.description`)}
						</Description>
					</Stack>
				) : (
					<Content>
						{tracks.map((track) => (
							<TrackView key={track.id} track={track} organizationId={organizationId} onSelected={setSelectedTrack} />
						))}
					</Content>
				)}
			</Container>
		);
	}
);

const Container = styled(RoundedBlock)`
	${stack(40, "flex-start", "stretch")}
	padding: 30px 40px;
`;

const IconView = styled.div`
	${row(0, "center", "center")}
	width: 100%;
	height: 100%;
	> img {
		@media ${desktop} {
			width: 347px;
			height: 217px;
		}
	}
`;

const Breadcrumb = styled.div`
	${({ theme }) => theme.textStyles.subtitle};

	> :first-child {
		cursor: pointer;
	}
`;
const TrackTitle = styled.span`
	color: ${({ theme }) => theme.colors.secondary};
`;

const Content = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 45px;

	@media ${mobile} {
		grid-template-columns: 100%;
	}
`;

const StyledPrimarybold = styled(PrimaryBold)`
	color: ${({ theme }) => theme.colors.secondary};
	font-size: 1.5rem;
	font-weight: 800;

	@media ${mobile} {
		font-size: 0.813rem;
	}
`;
